import { rtkApi, GET_TEACHER_REQUESTS } from '@/shared/api';
import { createFormData } from '@/shared/lib/utils/createFormData';
import { SuccessApiResponse } from '@/shared/types/common';
import {
  ICreateTeacherRequestPayload,
  IDeleteTeacherRequestResponse,
  ITeacherRequestDetail,
  ITeacherRequestListRow,
  IUpdateTeacherRequestPayload,
} from './types';

const hasFiles = (files?: File[]) => Boolean(files?.length);

const prepareMultipartPayload = (
  payload: ICreateTeacherRequestPayload | Omit<IUpdateTeacherRequestPayload, 'requestId'>,
) => ({
  ...payload,
  ...('removeAttachmentKeys' in payload
    ? {
        removeAttachmentKeys: payload.removeAttachmentKeys?.length
          ? JSON.stringify(payload.removeAttachmentKeys)
          : undefined,
      }
    : {}),
});

const teacherRequestsApi = rtkApi.injectEndpoints({
  endpoints: (build) => ({
    getTeacherRequests: build.query<ITeacherRequestListRow[], void>({
      query: () => '/teacher/requests',
      transformResponse: (response: SuccessApiResponse<{ requests: ITeacherRequestListRow[] }>) =>
        response.data.requests,
      providesTags: [GET_TEACHER_REQUESTS],
    }),
    getTeacherRequestDetail: build.query<ITeacherRequestDetail, number>({
      query: (requestId) => `/teacher/requests/${requestId}`,
      transformResponse: (response: SuccessApiResponse<ITeacherRequestDetail>) => response.data,
      providesTags: (_result, _error, requestId) => [{ type: GET_TEACHER_REQUESTS, id: requestId }],
    }),
    createTeacherRequest: build.mutation<ITeacherRequestDetail, ICreateTeacherRequestPayload>({
      query: (payload) => ({
        url: '/teacher/requests',
        method: 'POST',
        body: hasFiles(payload.staffAttachments)
          ? createFormData(prepareMultipartPayload(payload))
          : payload,
      }),
      transformResponse: (response: SuccessApiResponse<ITeacherRequestDetail>) => response.data,
      invalidatesTags: [GET_TEACHER_REQUESTS],
    }),
    updateTeacherRequest: build.mutation<ITeacherRequestDetail, IUpdateTeacherRequestPayload>({
      query: ({ requestId, ...payload }) => ({
        url: `/teacher/requests/${requestId}`,
        method: 'PUT',
        body: hasFiles(payload.staffAttachments)
          ? createFormData(prepareMultipartPayload(payload))
          : payload,
      }),
      transformResponse: (response: SuccessApiResponse<ITeacherRequestDetail>) => response.data,
      invalidatesTags: (_result, _error, { requestId }) => [
        GET_TEACHER_REQUESTS,
        { type: GET_TEACHER_REQUESTS, id: requestId },
      ],
    }),
    deleteTeacherRequest: build.mutation<IDeleteTeacherRequestResponse, number>({
      query: (requestId) => ({
        url: `/teacher/requests/${requestId}`,
        method: 'DELETE',
      }),
      transformResponse: (response: SuccessApiResponse<IDeleteTeacherRequestResponse>) =>
        response.data,
      invalidatesTags: [GET_TEACHER_REQUESTS],
    }),
  }),
});

export const {
  useGetTeacherRequestsQuery,
  useGetTeacherRequestDetailQuery,
  useCreateTeacherRequestMutation,
  useUpdateTeacherRequestMutation,
  useDeleteTeacherRequestMutation,
} = teacherRequestsApi;
